import React, { useState } from 'react';
import { useStripe } from '@stripe/react-stripe-js';
import { useAuth } from '@clerk/clerk-react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { BILLING_ENDPOINTS, buildUrl } from '../endpoints';
import { getClerkToken } from '../utils/clerkToken';
import { PricingTier } from '../types/pricing';

interface PlanUpgradeModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedTier: PricingTier | null;
  currentPlanId: string | null;
  isAnnualBilling: boolean;
  onSuccess?: () => void;
}

const PlanUpgradeModal: React.FC<PlanUpgradeModalProps> = ({
  isOpen,
  onClose,
  selectedTier,
  currentPlanId,
  isAnnualBilling,
  onSuccess
}) => {
  const stripe = useStripe();
  const { getToken } = useAuth();
  
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [step, setStep] = useState<'confirm' | 'payment' | 'success'>('confirm');

  if (!isOpen || !selectedTier) return null;

  const isUpgrade = currentPlanId ? selectedTier.id !== currentPlanId : true;
  const isDowngrade = currentPlanId && 
    ['business', 'professional', 'starter'].indexOf(currentPlanId) > 
    ['business', 'professional', 'starter'].indexOf(selectedTier.id);

  const price = isAnnualBilling ? selectedTier.annualPrice : selectedTier.monthlyPrice;
  const displayPrice = price === 'Free' || price === 'Custom' ? price : `$${price}`;
  const billingPeriod = isAnnualBilling ? 'year' : 'month';

  const handleConfirm = () => {
    setStep('payment');
  };

  const handlePayment = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!selectedTier) {
      return;
    }

    setIsProcessing(true);
    setError(null);

    try {
      const token = await getClerkToken(getToken);
      if (!token) {
        throw new Error('Authentication required. Please sign in again.');
      }

      const billingCycle = isAnnualBilling ? 'annual' : 'monthly';
      const requestPayload = {
        tier: selectedTier.id,
        billing_cycle: billingCycle,
      };

      console.info('[billing] create-checkout request', {
        tier: requestPayload.tier,
        billing_cycle: requestPayload.billing_cycle,
        endpoint: buildUrl(BILLING_ENDPOINTS.CREATE_CHECKOUT),
      });

      const response = await fetch(buildUrl(BILLING_ENDPOINTS.CREATE_CHECKOUT), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(requestPayload),
      });

      let data: any = null;
      try {
        data = await response.json();
      } catch (parseError) {
        console.warn('[billing] create-checkout response not JSON', parseError);
      }

      console.info('[billing] create-checkout response', {
        status: response.status,
        ok: response.ok,
        hasCheckoutUrl: Boolean(data?.checkout_url),
        hasSessionId: Boolean(data?.session_id),
      });

      if (!response.ok) {
        throw new Error(data?.message || data?.detail || 'Subscription creation failed');
      }

      if (stripe && data?.session_id) {
        const { error: redirectError } = await stripe.redirectToCheckout({
          sessionId: data.session_id,
        });
        if (redirectError) {
          throw new Error(redirectError.message || 'Stripe redirect failed');
        }
        return;
      }

      if (data?.checkout_url) {
        window.location.href = data.checkout_url;
        return;
      }

      throw new Error('Checkout session missing session ID or URL');

      setStep('success');
      setTimeout(() => {
        onSuccess?.();
        onClose();
        window.location.reload(); // Refresh to get updated subscription
      }, 2000);

    } catch (err) {
      console.error('[billing] checkout failed', err);
      setError(err instanceof Error ? err.message : 'Payment processing failed');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleClose = () => {
    setStep('confirm');
    setError(null);
    onClose();
  };

  return (
    <div className="fixed inset-0 !mt-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl max-w-md w-full shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-indigo-600 p-6 text-white">
          <div className="flex justify-between items-start">
            <div>
              <h2 className="text-xl font-bold">
                {step === 'success' ? 'Success!' : 
                 step === 'payment' ? 'Secure Checkout' :
                 isDowngrade ? 'Downgrade Plan' : 
                 currentPlanId ? 'Upgrade Plan' : 'Subscribe to Plan'}
              </h2>
              <p className="text-blue-100 text-sm mt-1">
                {step === 'success' ? 'Your subscription has been updated' :
                 step === 'payment' ? 'Continue to Stripe Checkout to complete payment' :
                 `${selectedTier.name} - ${displayPrice}/${billingPeriod}`}
              </p>
            </div>
            <button
              onClick={handleClose}
              className="text-white/80 hover:text-white text-2xl leading-none"
            >
              ×
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          {step === 'confirm' && (
            <div className="space-y-6">
              {/* Plan Summary */}
              <div className="bg-gray-50 rounded-xl p-4 border border-gray-100">
                <div className="flex items-center justify-between mb-3">
                  <span className="font-semibold text-gray-900">{selectedTier.name}</span>
                  {selectedTier.recommended && (
                    <Badge className="bg-blue-100 text-blue-700">Recommended</Badge>
                  )}
                </div>
                <div className="text-3xl font-bold text-gray-900 mb-2">
                  {displayPrice}
                  {displayPrice !== 'Free' && displayPrice !== 'Custom' && (
                    <span className="text-base font-normal text-gray-500">/{billingPeriod}</span>
                  )}
                </div>
                {isAnnualBilling && displayPrice !== 'Free' && displayPrice !== 'Custom' && (
                  <p className="text-sm text-green-600 font-medium">Save 20% with annual billing</p>
                )}
              </div>

              {/* Features */}
              <div>
                <h4 className="font-medium text-gray-900 mb-3">What's included:</h4>
                <ul className="space-y-2">
                  {selectedTier.features.slice(0, 5).map((feature, index) => (
                    <li key={index} className="flex items-start text-sm text-gray-600">
                      <svg className="w-5 h-5 text-green-500 mr-2 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      {feature}
                    </li>
                  ))}
                  <li className="flex items-start text-sm text-gray-600">
                    <svg className="w-5 h-5 text-green-500 mr-2 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    {selectedTier.audioFileUploads} uploads • {selectedTier.storagePerUser} storage
                  </li>
                </ul>
              </div>

              {error && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-sm text-red-600">{error}</p>
                </div>
              )}

              <Button
                onClick={handleConfirm}
                disabled={isProcessing}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white py-3"
              >
                {isProcessing ? (
                  <span className="flex items-center justify-center gap-2">
                    <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    Processing...
                  </span>
                ) : (
                  'Continue to Checkout'
                )}
              </Button>

              <p className="text-xs text-gray-500 text-center">
                {selectedTier.trialDuration} free trial • Cancel anytime
              </p>
            </div>
          )}

          {step === 'payment' && (
            <form onSubmit={handlePayment} className="space-y-6">
              {/* Plan Summary */}
              <div className="bg-gray-50 rounded-xl p-4 border border-gray-100">
                <div className="flex justify-between items-center">
                  <span className="font-medium text-gray-900">{selectedTier.name}</span>
                  <span className="text-xl font-bold text-blue-600">
                    {displayPrice}/{billingPeriod}
                  </span>
                </div>
              </div>

              <div className="text-sm text-gray-700">
                You will be redirected to Stripe Checkout to complete payment securely.
              </div>

              {error && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-sm text-red-600">{error}</p>
                </div>
              )}

              <div className="space-y-3">
                <Button
                  type="submit"
                  disabled={isProcessing}
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white py-3"
                >
                  {isProcessing ? (
                    <span className="flex items-center justify-center gap-2">
                      <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                      </svg>
                      Creating Checkout Session...
                    </span>
                  ) : (
                    `Continue to Stripe Checkout`
                  )}
                </Button>

                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setStep('confirm')}
                  disabled={isProcessing}
                  className="w-full"
                >
                  Back
                </Button>
              </div>

              <div className="flex items-center justify-center gap-2 text-xs text-gray-500">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
                Secured by Stripe • Payment handled on Stripe Checkout
              </div>
            </form>
          )}

          {step === 'success' && (
            <div className="text-center py-6">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">
                Welcome to {selectedTier.name}!
              </h3>
              <p className="text-gray-600 mb-6">
                Your subscription has been activated successfully.
              </p>
              <div className="flex items-center justify-center gap-2 text-sm text-gray-500">
                <svg className="animate-spin h-4 w-4 text-blue-600" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
                Refreshing your dashboard...
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PlanUpgradeModal;
